import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import StyleHeroBanner from "../components/StyleHeroBanner";
import StyleThumbnails from "../components/StyleThumbnails";
import Tabs from "../components/Tabs";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import { getPageTitle } from "../resources/routes";
import {
  ExteriorStyle,
  HouseType,
  LandType,
  UpgradeStyle,
} from "../types";
import { scrollToTop, scrollToWithOffset } from "../util";

const ChooseExteriorStyleScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const tabTitles = ["Exterior Colour", "Roof Type"];

  const exteriorStyles = apiContext.exteriorStyleResults.filter((item) => {
    if (userContext.isCladdedSelected) {
      return item.rendivo_tag.toLowerCase().indexOf("cladded") > -1;
    } else {
      return item.rendivo_tag.toLowerCase().indexOf("cladded") === -1;
    }
  });

  useEffect(() => {
    scrollToTop();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedExteriorStyle(null);
      userContext.resetSelectedRoofStyle();
      userContext.resetSelectedSelectedGutterStyle();
      userContext.resetSelectedSelectedWindowStyle();
    });

    // Select default option
    if (userContext.selectedExteriorStyle) {
      onClickExteriorColour(userContext.selectedExteriorStyle);
    } else if (exteriorStyles.length) {
      onClickExteriorColour(exteriorStyles[0]);
    }

    return () => {
      navContext.resetBackBtnOnClick();
    };
  }, []);

  useEffect(() => {
    navContext.resetNextBtn();
    navContext.resetBackBtnOnClick();

    if (tabIndex < tabTitles.length - 1) {
      navContext.setNextBtnOnClick(() => {
        setTabIndex(tabIndex + 1);
      });
    }

    if (tabIndex > 0) {
      navContext.setBackBtnOnClick(() => {
        setTabIndex(tabIndex - 1);
      });
    }
  }, [tabIndex, tabTitles]);

  useEffect(() => {
    navContext.setSubtotalVisible(userContext.subtotalPrice > 0);
  }, [userContext.subtotalPrice]);

  const onClickExteriorColour = (exteriorStyle: ExteriorStyle) => {
    if (
      !userContext.selectedRoofStyle.has(exteriorStyle.id) &&
      exteriorStyle.roof_options.length > 0
    ) {
      // Make sure we always select a default if the user has never visited this exterior style before
      userContext.setSelectedRoofStyle(
        exteriorStyle.id,
        exteriorStyle.roof_options[0]
      );
    }

    if (
      !userContext.selectedGutterStyle.has(exteriorStyle.id) &&
      exteriorStyle.gutter_options.length > 0
    ) {
      // Make sure we always select a default if the user has never visited this exterior style before
      userContext.setSelectedGutterStyle(
        exteriorStyle.id,
        exteriorStyle.gutter_options[0]
      );
    }

    if (
      !userContext.selectedWindowStyle.has(exteriorStyle.id) &&
      exteriorStyle.window_options.length > 0
    ) {
      // Make sure we always select a default if the user has never visited this exterior style before
      userContext.setSelectedWindowStyle(
        exteriorStyle.id,
        exteriorStyle.window_options[0]
      );
    }

    userContext.setSelectedExteriorStyle(exteriorStyle);
  };

  const onClickRoof = (option: UpgradeStyle) => {
    if (userContext.selectedExteriorStyle) {
      userContext.setSelectedRoofStyle(
        userContext.selectedExteriorStyle.id,
        option
      );
    }
  };

  const onClickGutter = (option: UpgradeStyle) => {
    if (userContext.selectedExteriorStyle) {
      userContext.setSelectedGutterStyle(
        userContext.selectedExteriorStyle.id,
        option
      );
    }
  };

  const onClickWindow = (option: UpgradeStyle) => {
    if (userContext.selectedExteriorStyle) {
      userContext.setSelectedWindowStyle(
        userContext.selectedExteriorStyle.id,
        option
      );
    }
  };

  const renderExteriorColours = () => {
    const selectedExterior = userContext.selectedExteriorStyle;
    if (!selectedExterior) {
      return <></>;
    }

    return (
      <StyleThumbnails
        imageStyle={"short"}
        activeStyle={"white"}
        items={exteriorStyles}
        selectedId={selectedExterior.id}
        onClickItem={(index) => onClickExteriorColour(exteriorStyles[index])}
      />
    );
  };

  const renderRoofStyles = () => {
    const selectedExterior = userContext.selectedExteriorStyle;
    if (!selectedExterior) {
      return <></>;
    }
    const selectedStyle = userContext.selectedRoofStyle.get(
      selectedExterior.id
    );

    const selectedId = selectedStyle ? selectedStyle.id : -1;

    const options = selectedExterior.roof_options.map((option) => {
      if (
        userContext.selectedHome &&
        userContext.selectedHome.roof_upgrades[option.id] !== undefined
      ) {
        return {
          ...option,
          price: userContext.selectedHome.roof_upgrades[option.id],
        };
      } else if (
        userContext.selectedHouseLand &&
        userContext.selectedHouseLand.roof_upgrades[option.id] !== undefined
      ) {
        return {
          ...option,
          price: userContext.selectedHouseLand.roof_upgrades[option.id],
        };
      }
      return {
        ...option,
        price: 0,
      };
    });

    return (
      <StyleThumbnails
        imageStyle={"short"}
        activeStyle={"white"}
        showPrice
        hideIncluded
        items={options}
        selectedId={selectedId}
        onClickItem={(index) =>
          onClickRoof(selectedExterior.roof_options[index])
        }
      />
    );
  };

  const renderGutterStyles = () => {
    const selectedExterior = userContext.selectedExteriorStyle;
    if (!selectedExterior) {
      return <></>;
    }
    const selectedStyle = userContext.selectedGutterStyle.get(
      selectedExterior.id
    );

    const selectedId = selectedStyle ? selectedStyle.id : -1;

    return (
      <StyleThumbnails
        imageStyle={"short"}
        activeStyle={"white"}
        items={selectedExterior.gutter_options}
        selectedId={selectedId}
        onClickItem={(index) =>
          onClickGutter(selectedExterior.gutter_options[index])
        }
      />
    );
  };

  const renderWindowStyles = () => {
    const selectedExterior = userContext.selectedExteriorStyle;
    if (!selectedExterior) {
      return <></>;
    }
    const selectedStyle = userContext.selectedWindowStyle.get(
      selectedExterior.id
    );

    const selectedId = selectedStyle ? selectedStyle.id : -1;

    return (
      <StyleThumbnails
        imageStyle={"short"}
        activeStyle={"white"}
        items={selectedExterior.window_options}
        selectedId={selectedId}
        onClickItem={(index) =>
          onClickWindow(selectedExterior.window_options[index])
        }
      />
    );
  };

  const renderTabContent = (tabIndex: number) => {
    const selectedExterior = userContext.selectedExteriorStyle;
    if (!selectedExterior) {
      return <></>;
    }
    const selectedRoofStyle = userContext.selectedRoofStyle.get(
      selectedExterior.id
    );

    const selectedGutterStyle = userContext.selectedGutterStyle.get(
      selectedExterior.id
    );

    const selectedWindowStyle = userContext.selectedWindowStyle.get(
      selectedExterior.id
    );

    let roofId = -1;
    let gutterId = -1;
    let windowId = -1;

    if (tabIndex === 1 && selectedRoofStyle) {
      roofId = selectedRoofStyle.id;
    } else if (tabIndex === 2 && selectedGutterStyle) {
      gutterId = selectedGutterStyle.id;
    } else if (tabIndex === 3 && selectedWindowStyle) {
      windowId = selectedWindowStyle.id;
    }

    return (
      <div className={"tab-content container"}>
        <StyleHeroBanner
          showRendivoDisclaimer={apiContext.rendivoDisclaimerVisible}
          images={[userContext.rendivoExteriorRender]}
          usingRendivoImages={true}
          key={`${selectedExterior.id}_${roofId}_${gutterId}_${windowId}`}
          fancybox
        />
        {[
          renderExteriorColours,
          renderRoofStyles,
          renderGutterStyles,
          renderWindowStyles,
        ][tabIndex]()}
      </div>
    );
  };

  let houseType = HouseType.DOUBLE_STORY;
  let lotWidth = userContext.selectedHome
    ? userContext.selectedHome.lot_width
    : "";

  if (
    userContext.landType === LandType.HOUSE_LAND_PACKAGE &&
    userContext.selectedHouseLand
  ) {
    houseType =
      userContext.selectedHouseLand.rendivo_levels == 1
        ? HouseType.SINGLE_STORY
        : HouseType.DOUBLE_STORY;
    lotWidth = userContext.selectedHouseLand.lot_width;
  } else if (
    userContext.landType !== LandType.HOUSE_LAND_PACKAGE &&
    userContext.selectedFacade
  ) {
    houseType =
      userContext.selectedFacade.rendivo_levels == 1
        ? HouseType.SINGLE_STORY
        : HouseType.DOUBLE_STORY;
  }

  const onChangeTabIndex = (tabIndex: number) => {
    setTabIndex(tabIndex);
    setTimeout(function () {
      scrollToWithOffset(".tabs-header", -20);
    }, 250);
  };

  return (
    <div
      className={`screen screen-choose-exterior-style house-type-${houseType} lot-width-${lotWidth}`}
    >
      <div className={"container"}>
        <h1>{getPageTitle(location.pathname)}</h1>
        <p className={"subtitle"}>
          Choose a colour scheme that reflects your very own taste and
          personality. Inspired by the natural landscape.
        </p>
      </div>

      <Tabs
        headerClassName={"container"}
        titles={tabTitles}
        renderContent={renderTabContent}
        tabIndex={tabIndex}
        onChangeTabIndex={(index) => onChangeTabIndex(index)}
      />
    </div>
  );
});

export default ChooseExteriorStyleScreen;
